import { convertGeometryToOpenLayers, Geometry3 } from "../geom"
import { selectMany } from "../utils/array"
import { calculateDistance } from "../utils/geom"
import { isArray, isNumber } from "../utils/utils"
import { IMapMeasuringService } from "./measuring/types"
import { OpenLayersStyleProvider } from "./types"
import { ol_polygonFromCircle, ol_geom, ol_Feature, ol_style, ol_extent, ol_Extent, ol_Coordinate, ol_FeatureLike, ol_Observable_unByKey, ol_proj, ol_Observable } from "@omnia/exports/openlayers";

export type Coord = [number, number]

export function setFeatureStyle(feature: ol_Feature, style: OpenLayersStyleProvider | ol_style.Style[]) {
    (<any>feature).setStyle(style)
}

export function isCoordinate(value: any): value is ol_Coordinate {
    return isArray(value) && (value.length == 2) && value.every(i => isNumber(i));
}

export function isExtent(value: any): value is ol_Extent {
    return isArray(value) && (value.length == 4) && value.every(i => isNumber(i));
}

export function checkFeatureInExtent(feature: ol_Feature, extent: ol_Extent): boolean {
    return checkGeometryInExtent(feature.getGeometry(), extent);
}

export function checkGeometryInExtent(geom: any, extent: ol_Extent): boolean {

    let type = (geom.getType ? geom.getType() : null);

    switch (type) {

        case "Polygon":
            return checkPolygonInExtent(<ol_geom.Polygon>geom, extent);

        case "MultiPolygon":
            return checkMultiPolygonInExtent(<ol_geom.MultiPolygon>geom, extent);

        case "GeometryCollection":
            return checkGeometryCollectionPolygonInExtent(<ol_geom.GeometryCollection>geom, extent);

        default:
            return false;

    }

}

export function checkGeometryCollectionPolygonInExtent(collection: ol_geom.GeometryCollection, extent: ol_Extent): boolean {
    return collection.getGeometries().every(g => checkGeometryInExtent(g, extent));
}

export function checkPolygonInExtent(polygon: ol_geom.Polygon, extent: ol_Extent): boolean {
    return polygon.getLinearRing(0).getCoordinates().every(c => ol_extent.containsCoordinate(extent, c));
}


export function checkMultiPolygonInExtent(polygon: ol_geom.MultiPolygon, extent: ol_Extent): boolean {
    return polygon.getPolygons().every(p => checkPolygonInExtent(p, extent));
}

export function getRectagleFromExtent(ext: ol_Extent): ol_geom.Polygon {

    const coords = [[
        [ext[0], ext[1]],
        [ext[2], ext[1]],
        [ext[2], ext[3]],
        [ext[0], ext[3]],
        [ext[0], ext[1]]
    ]] as Coord[][]

    let polygon = new ol_geom.Polygon(coords);

    return polygon;

}

export function getRectangleAtLocation(center: ol_Extent, width: number, height: number): ol_geom.Polygon {

    let extent = getExtentAtLocation(center, width, height);

    let rect = getRectagleFromExtent(extent);

    return rect;

}

export function getExtentAtLocation(center: ol_Extent, width: number, height: number): ol_Extent {

    width /= 2;
    height /= 2;

    return [
        center[0] - width,
        center[1] - height,
        center[0] + width,
        center[1] + height
    ];

}

export function getPolygonsAsArray(geometries: ol_geom.Geometry[]): ol_geom.Polygon[] {

    return selectMany(

        geometries.map(g => {

            if (g instanceof ol_geom.Polygon) {
                return [g];
            }

            if (g instanceof ol_geom.MultiPolygon) {
                return g.getPolygons();
            }

            if (g instanceof ol_geom.GeometryCollection) {
                return getPolygonsAsArray(g.getGeometries());
            }

        })
            .filter(a => !!a)

    );

}

export function getLargestPolygonFromArray(geometries: ol_geom.Geometry[], minArea: number = 1): ol_geom.Polygon {

    let geom = getPolygonsAsArray(geometries);

    geom = geom
        .map(g => {
            return {
                g: g,
                area: g.getArea()
            }
        })
        .filter(g => g.area >= minArea)
        .sort((g1, g2) => g2.area - g1.area)
        .map(g => g.g);

    if (geom.length) {
        return geom[0];
    }
}


export function getLinesAsArray(geometries: ol_geom.Geometry[]): ol_geom.LineString[] {

    return selectMany(

        geometries.map(g => {

            if (g instanceof ol_geom.LineString) {
                return [g];
            }

            if (g instanceof ol_geom.MultiLineString) {
                return g.getLineStrings();
            }

            if (g instanceof ol_geom.GeometryCollection) {
                return getLinesAsArray(g.getGeometries());
            }

        })
            .filter(a => !!a)

    );

}


export function getLargestLineFromArray(geometries: ol_geom.Geometry[]): ol_geom.LineString {

    let geom = getLinesAsArray(geometries);

    geom = geom
        .map(g => {
            return {
                g: g,
                length: g.getLength()
            }
        })
        .sort((g1, g2) => g2.length - g1.length)
        .map(g => g.g);

    if (geom.length) {
        return geom[0];
    }
}

export function observeOpenlayersObject(obj: ol_Observable, type: string | string[], watcher: Function): Function {

    const key = obj.on(type as unknown as any, e => watcher(e))

    return () => {
        ol_Observable_unByKey(key)
    }

}

export function getExtentsUnion(extents: ol_Extent[]): ol_Extent {

    if (!extents.length) return null;

    return [
        Math.min.apply(Math, extents.map(e => e[0])),
        Math.min.apply(Math, extents.map(e => e[1])),
        Math.max.apply(Math, extents.map(e => e[2])),
        Math.max.apply(Math, extents.map(e => e[3])),
    ];

}

export function getGeometryPoints(geom: ol_geom.Geometry, includeInnerRings: boolean) {

    let coords: ol_Coordinate[];

    if (geom) {

        if (geom instanceof ol_geom.LineString) {
            coords = geom.getCoordinates();
        }

        else if (geom instanceof ol_geom.Circle) {

            const polygon = ol_polygonFromCircle(geom, 12)
            coords = polygon.getLinearRing(0).getCoordinates()

        }

        else if (geom instanceof ol_geom.Polygon) {

            if (includeInnerRings) {
                coords = selectMany(geom.getCoordinates());
            }
            else {
                let ring = geom.getLinearRing(0);
                coords = ring ? ring.getCoordinates() : null;
            }
        }

        if (coords) {
            let points = new ol_geom.MultiPoint(coords);
            return points;
        }

    }

}

export function getFeaturePoints(feature: ol_FeatureLike, includeInnerRings: boolean) {

    if (feature) {
        return getGeometryPoints((<any>feature).getGeometry(), includeInnerRings);
    }

}

export function getGeometryCenter(geom: ol_geom.Geometry): ol_Coordinate {

    if (geom instanceof ol_geom.Polygon) {
        return geom.getInteriorPoint().getFirstCoordinate();
    }

    if (geom instanceof ol_geom.LineString) {
        return getLineStringCenter(geom)
    }

    return ol_extent.getCenter(geom.getExtent());
}


export function getLineStringCenter(ls: ol_geom.LineString): ol_Coordinate {

    const { segments, totalLength } = getLineStringSegments(ls)

    const halfLength = totalLength / 2
    let currentLength = 0

    for (const s of segments) {

        const { p1, p2, length } = s
        let l = currentLength + length

        if (l >= halfLength) {

            l = (halfLength - currentLength) / length

            const dx = p2[0] - p1[0]
            const dy = p2[1] - p1[1]
            return [
                s.p1[0] + (dx * l),
                s.p1[1] + (dy * l)
            ]
        }

        currentLength = l

    }

}

export function getLineStringSegments(ls: ol_geom.LineString): {
    segments: { p1: ol_Coordinate, p2: ol_Coordinate, length: number }[],
    totalLength: number
} {

    const coords = ls.getCoordinates()
    const { length } = coords
    if (length < 2) return null

    const segments: { p1: ol_Coordinate, p2: ol_Coordinate, length: number }[] = []
    let totalLength = 0

    for (let i = 1; i < length; i++) {
        const p1 = coords[i - 1]
        const p2 = coords[i]
        const segment = {
            p1,
            p2,
            length: calculateDistance(p1, p2)
        }
        segments.push(segment)
        totalLength += segment.length
    }

    return {
        segments,
        totalLength
    }
}

export function isFeatureLine(feature: ol_Feature) {
    if (feature) {
        let geom = feature.getGeometry();
        return geom instanceof ol_geom.LineString;
    }
}

export function isFeaturePoint(feature: ol_Feature) {
    if (feature) {
        let geom = feature.getGeometry();
        return geom instanceof ol_geom.Point;
    }
}

export function getGeometryArea(geometry: ol_geom.Geometry | ol_Feature, measuringProj: string, sourceProj: string = "EPSG:3857") {
    return getGeometryDimensions(geometry, measuringProj, sourceProj).area
}

export function getGeometryLength(geometry: ol_geom.Geometry | ol_Feature, measuringProj: string, sourceProj: string = "EPSG:3857"): number {
    return getGeometryDimensions(geometry, measuringProj, sourceProj).length
}

export function getCircleArea(circle: ol_geom.Circle, measuringProj, sourceProj: string = "EPSG:3857"): number {
    return getGeometryDimensions(circle, measuringProj, sourceProj).area
}

export function getGeometryDimensions(geometry: ol_geom.Geometry | ol_Feature, measuringProj: string, sourceProj: string = "EPSG:3857") {

    let g: any = geometry instanceof ol_Feature
        ? geometry.getGeometry()
        : geometry

    if (g && g.clone) {

        if (g instanceof ol_geom.Circle) {
            return getCircleDimensions(g, measuringProj, sourceProj)
        }

        g = g.clone().transform(sourceProj, measuringProj)

        return {
            area: g.getArea && g.getArea(),
            length: g.getLength && g.getLength()
        }

    }

}

export function getCircleDimensions(circle: ol_geom.Circle, measuringProj: string, sourceProj: string = "EPSG:3857") {

    const center = circle.getCenter()
    const coords = [center, [center[0] + circle.getRadius(), center[1]]] as Coord[]
    const line = new ol_geom.LineString(coords)
    const radius = getGeometryLength(line, measuringProj, sourceProj)

    return {
        area: Math.PI * radius * radius,
        length: Math.PI * 2 * radius
    }

}

export function getProjectedDistance(center: ol_Coordinate, distance: number, sourceProj: string, measuringProj: string): number {

    const c1: ol_Coordinate = ol_proj.transform(center, sourceProj, measuringProj);
    const c2: ol_Coordinate = [c1[0] + distance, c1[1]];

    let line = new ol_geom.LineString([c1, c2]);

    line.transform(measuringProj, sourceProj);

    return line.getLength();

}

export function getExtentDimensions(measure: IMapMeasuringService, extent: ol_Extent, sourceProj: string = "EPSG:3857"): { width: number, height: number } {

    const centerx = (extent[0] + extent[2]) / 2
    const centery = (extent[1] + extent[3]) / 2

    const width = measure.getDistance([extent[0], centery], [extent[2], centery], sourceProj)
    const height = measure.getDistance([centerx, extent[1]], [centerx, extent[3]], sourceProj)

    return {
        width,
        height
    }

}

export function createFeatures<T>(
    source: T[],
    geometrySelector: (i: T) => Geometry3,
    idSelector?: (i: T) => any,
    valueSelector?: (i: T) => any,
    defaultProjection = "EPSG:3857",
    featureData: {} = {}): ol_Feature[] {

    if (!valueSelector) {
        valueSelector = (f) => f;
    }

    const featureDataKeys = Object.keys(featureData)

    return source
        .map(f => {

            const s = geometrySelector(f)

            const geom = convertGeometryToOpenLayers(s, defaultProjection)

            if (geom) {

                const feature = new ol_Feature({
                    geometry: geom
                });

                featureDataKeys.forEach(k => feature.set(k, featureData[k]))

                feature.set("data", valueSelector(f))

                if (idSelector) {
                    feature.setId(idSelector(f))
                }

                return feature
            }

        })
        .filter(f => !!f)

}
